/**
 * Represents a resource from the /document-templates/ API collection.
 *
 * Uses a local store for now, as the API is not yet deployed.
 */


import type { ActivatedRoute } from '@angular/router';

import { CustomCustomerFieldType } from './organization-config-enums';
import { loadBoolFilterFromGetParams, loadIntFilterFromGetParams, loadStringFilterFromGetParams } from '../reporting-core/utils';

export enum DocumentType {
  waiver = 'WAI',
  supervisorForm = 'SUP',
  proficiency = 'PRO',
  membership_agreement = 'MEM',
  custom = 'CUS'
}

export enum DocumentSectionType {
  // Waiver/AoR required sections
  adultClimberInfo = 'adultClimberInfo',
  minorClimberInfo = 'minorClimberInfo',
  unsupervisedMinorClimberInfo = 'unsupervisedMinorClimberInfo',
  adultNonClimberInfo = 'adultNonClimberInfo',
  supervisedAdultClimberInfo = 'supervisedAdultClimberInfo',

  // Supervisor form required sections
  supervisingClimberInfo = 'supervisingClimberInfo',
  supervisedClimberInfo = 'supervisedClimberInfo',

  // Required section for all other document types
  climberInfo = 'climberInfo',

  // Generic sections
  pageBreak = 'pageBreak',
  custom = 'custom'
}

export const ClimberInfoDocumentSectionTypes: DocumentSectionType[] = [
  DocumentSectionType.adultClimberInfo,
  DocumentSectionType.minorClimberInfo,
  DocumentSectionType.adultNonClimberInfo
];

export enum DocumentElementType {
  capitanFirstName = 'capitanFirstName',
  capitanLastName = 'capitanLastName',
  capitanPreferredName = 'capitanPreferredName',
  capitanEmailAddress = 'capitanEmailAddress',
  capitanBirthday = 'capitanBirthday',
  capitanAddress = 'capitanAddress',
  capitanEmergencyContact = 'capitanEmergencyContact',
  capitanGovernmentID = 'capitanGovernmentID',
  capitanPhoneNumber = 'capitanPhoneNumber',
  capitanCustomerPhoto = 'capitanCustomerPhoto',
  capitanMedicalNotes = 'capitanMedicalNotes',
  capitanGender = 'capitanGender',
  capitanPronouns = 'capitanPronouns',
  multipleChoice = 'multipleChoice',
  shortText = 'shortText',
  paragraphInput = 'paragraphInput',
  singleCheckbox = 'singleCheckbox',
  multipleCheckbox = 'multipleCheckbox',
  quizQuestion = 'quizQuestion',
  policy = 'policy',
  video = 'video',
  displayText = 'displayText',
  signature = 'signature',
  initial = 'initial',
  customCustomerField = 'customCustomerField',
  relationship = 'relationship'
}

export const CapitanDocumentElementTypes: DocumentElementType[] = [
  DocumentElementType.capitanFirstName,
  DocumentElementType.capitanLastName,
  DocumentElementType.capitanPreferredName,
  DocumentElementType.capitanEmailAddress,
  DocumentElementType.capitanBirthday,
  DocumentElementType.capitanAddress,
  DocumentElementType.capitanEmergencyContact,
  DocumentElementType.capitanGovernmentID,
  DocumentElementType.capitanPhoneNumber,
  DocumentElementType.capitanCustomerPhoto,
  DocumentElementType.capitanMedicalNotes,
  DocumentElementType.capitanGender,
  DocumentElementType.capitanPronouns,
  DocumentElementType.relationship
];

export const MinorOnlyDocumentElementTypes: DocumentElementType[] = [
  DocumentElementType.relationship
];

export const CapitanGenericDocumentElementTypes: DocumentElementType[] = [
  DocumentElementType.capitanFirstName,
  DocumentElementType.capitanLastName,
  DocumentElementType.capitanPreferredName,
  DocumentElementType.capitanEmailAddress,
  DocumentElementType.capitanGovernmentID,
  DocumentElementType.capitanPhoneNumber,
  DocumentElementType.capitanPronouns
];

export const CustomQuestionDocumentElementTypes: DocumentElementType[] = [
  DocumentElementType.shortText,
  DocumentElementType.paragraphInput,
  DocumentElementType.singleCheckbox,
  DocumentElementType.quizQuestion
];

export const ContentDocumentElementTypes: DocumentElementType[] = [
  DocumentElementType.video,
  DocumentElementType.displayText,
  DocumentElementType.signature
];

export const ClimberInfoRequiredDocumentElementTypes: DocumentElementType[] = [
  DocumentElementType.capitanFirstName,
  DocumentElementType.capitanLastName,
  DocumentElementType.capitanEmailAddress,
  DocumentElementType.capitanBirthday
];

export enum DocumentPolicyStatus {
  active = 'ACT',
  retired = 'RET'
}

export enum DocumentUserType {
  adult = 'ADU',
  adultWithMinors = 'ADM',
  minors = 'MIN',
  unsupervisedMinors = 'UMI',
  adultWithSupervisedAdults = 'SAD',
  supervisor = 'SUP',
  supervisorWithSupervisee = 'SWI'
}

export interface DocumentTemplateElementParameters {
  required?: boolean;
  post_code_only?: boolean;
  content?: string;
  video_embed_code?: string;
  label_text?: string;
  checkbox_text?: string;
  question_text?: string;
  question_choices?: string[];
  question_correct_answers?: string[];
  help_text?: string;
  policy_id?: number;
  number_of_contacts?: number;
  collect_email?: boolean;
  collect_relation?: boolean;
  include_marketing_opt_in?: boolean;
  include_mailchimp_groups?: boolean;
  field_id?: number;
  name?: string;
  field_type?: CustomCustomerFieldType;
  options?: string[];
}

export interface DocumentTemplateElement {
  element_type: DocumentElementType;
  parameters: DocumentTemplateElementParameters;

  // Non-api internal variables
  editing?: boolean;
}

export interface DocumentTemplateSection {
  name: string;
  section_type: DocumentSectionType;
  elements: DocumentTemplateElement[];

  // Non-api internal variables
  editingName?: boolean;
}

// A product (currently always an event type) that references a document
// template, shown on the template's "Linked Products" tab (PRO-863).
export interface DocumentTemplateLinkedEventType {
  event_type_id: number;
  event_type_name: string;
  relationship: string;
}

export interface DocumentTemplateLinkedProducts {
  event_types: DocumentTemplateLinkedEventType[];
}

export interface DocumentTemplate {
  id: number;
  url: string;
  climber_app_url: string;
  document_type: DocumentType;
  name: string;
  is_location_restricted: boolean;
  allowed_locations: {id: number, name: string}[];
  can_be_used_for_barcode_check_in: boolean;
  // Whether the template has been retired (PRO-863). Read-only; toggled via the
  // retire/restore service methods.
  is_removed: boolean;
  is_available_in_kiosk_mode: boolean;
  is_available_in_climber_app: boolean;
  quiz_error_message_kiosk: string;
  quiz_error_message_climber_app: string;
  quiz_error_alert_immediately_climber_app: boolean;
  quiz_error_create_inbox_item_climber_app: boolean;
  is_available_to_adult_participants: boolean;
  is_available_to_adults_with_minors: boolean;
  is_available_to_minors: boolean;
  is_available_to_unsupervised_minors: boolean;
  is_available_to_adults_with_supervised_adults: boolean;
  is_available_to_supervisors_alone: boolean;
  is_available_to_supervisors_with_supervisees: boolean;
  adult_min_age: number | null;
  minor_max_age: number | null;
  max_secondary_customer_count: number;
  sections: DocumentTemplateSection[];
  welcome_text: string;
  confirmation_text: string;
  days_to_expiration: number;
  years_to_expiration: number;
  expire_when_minor_becomes_adult: boolean;
  send_expiring_soon_email: boolean;
  expiring_soon_email_lead_days: number;
  expiring_soon_email_text: string;
  expiring_soon_email_include_button: boolean;
  expiring_soon_email_button_text: string;
  send_expired_email: boolean;
  expired_email_text: string;
  expired_email_include_button: boolean;
  expired_email_button_text: string;
  counts_as_waiver_for_supervisees: boolean;
  requires_staff_approval: boolean;
  order_number: number;
  granted_adult_proficiencies: number[];
  granted_adult_non_participant_proficiencies: number[];
  granted_minor_proficiencies: number[];
  granted_unsupervised_minor_proficiencies: number[];
  granted_supervised_adult_proficiencies: number[];
  granted_supervisor_proficiencies: number[];
  granted_supervisee_proficiencies: number[];
  granted_climber_proficiencies: number[];
  created_at: string;
  updated_at: string;
}

export interface DocumentPolicy {
  id: number;
  url: string;
  name: string;
  status: DocumentPolicyStatus;
  content: string;
  containing_document_template_ids: number[];
  climber_app_url: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentCustomer {
  id: string;
  first_name: string;
  last_name: string;
  birthday: string;
  is_expired: boolean;
}

export interface Document {
  id: string;
  template_name: string;
  document_type: DocumentType;
  is_usable_for_entry: boolean;
  can_be_used_for_barcode_check_in: boolean;
  user_type: DocumentUserType;
  pdf: string | null;
  is_complete: boolean;
  is_invalid: boolean;
  is_expired_for_all: boolean;
  is_pending_staff_approval: boolean;
  is_denied_staff_approval: boolean;
  related_customers: DocumentCustomer[];
  climber_customers: DocumentCustomer[];
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Get the human-readable name of the given document type.
 */
export function getDocumentTypeDisplay(documentType: DocumentType) {
  switch (documentType) {
    case DocumentType.waiver:
      return 'Waiver/AoR';
    case DocumentType.supervisorForm:
      return 'Supervisor Form';
    case DocumentType.proficiency:
      return 'Proficiency';
    case DocumentType.membership_agreement:
      return 'Membership Agreement';
    case DocumentType.custom:
      return 'Custom';
    default:
      return 'Unknown';
  }
}

/**
 * Get the human-readable name of the given document section's type.
 *
 * @param section - The document template section.
 */
export function getDocumentSectionTypeDisplay(section: DocumentTemplateSection) {
  switch (section.section_type) {
    case DocumentSectionType.adultClimberInfo:
      return 'Adult Participant Information';
    case DocumentSectionType.minorClimberInfo:
      return 'Minor Participant Information';
    case DocumentSectionType.unsupervisedMinorClimberInfo:
      return 'Unsupervised Minor Participant Information';
    case DocumentSectionType.adultNonClimberInfo:
      return 'Adult Non-Participant Information';
    case DocumentSectionType.supervisedAdultClimberInfo:
      return 'Supervised Adult Participant Information';
    case DocumentSectionType.supervisingClimberInfo:
      return 'Supervising Participant Information';
    case DocumentSectionType.supervisedClimberInfo:
      return 'Supervised Participant Information';
    case DocumentSectionType.climberInfo:
      return 'Participant Information';
    case DocumentSectionType.pageBreak:
      return 'Page Break';
    case DocumentSectionType.custom:
      return 'Custom Section';
    default:
      return 'Unknown Section';
  }
}

/**
 * Get the human-readable name of the given document element type.
 *
 * @param elementType - The document template element type.
 */
export function getDocumentElementTypeDisplay(elementType: DocumentElementType) {
  switch (elementType) {
    case DocumentElementType.capitanFirstName:
      return 'First Name';
    case DocumentElementType.capitanLastName:
      return 'Last Name';
    case DocumentElementType.capitanPreferredName:
      return 'Preferred Name';
    case DocumentElementType.capitanEmailAddress:
      return 'Email';
    case DocumentElementType.capitanBirthday:
      return 'Birthday';
    case DocumentElementType.capitanAddress:
      return 'Address';
    case DocumentElementType.capitanEmergencyContact:
      return 'Emergency Contact';
    case DocumentElementType.capitanGender:
      return 'Gender';
    case DocumentElementType.capitanPronouns:
      return 'Pronouns';
    case DocumentElementType.capitanGovernmentID:
      return 'Government ID';
    case DocumentElementType.capitanPhoneNumber:
      return 'Phone Number';
    case DocumentElementType.capitanCustomerPhoto:
      return 'Customer Photo';
    case DocumentElementType.capitanMedicalNotes:
      return 'Medical Notes';
    case DocumentElementType.policy:
      return 'Policy';
    case DocumentElementType.displayText:
      return 'Display Text';
    case DocumentElementType.signature:
      return 'Signature';
    case DocumentElementType.initial:
      return 'Initial';
    case DocumentElementType.video:
      return 'Embedded Video';
    case DocumentElementType.shortText:
      return 'Short Text Input';
    case DocumentElementType.paragraphInput:
      return 'Paragraph Input';
    case DocumentElementType.singleCheckbox:
      return 'Single Checkbox';
    case DocumentElementType.quizQuestion:
      return 'Quiz Question';
    case DocumentElementType.customCustomerField:
      return 'Custom Customer Field';
    case DocumentElementType.relationship:
      return 'Relationship (Minors Only)';
    default:
      return 'Unknown Element';
  }
}

/** "Name", or "Name (Retired)" for a retired template. */
export function getDocumentTemplateFilterLabel(template: DocumentTemplate): string {
  return template.is_removed ? template.name + ' (Retired)' : template.name;
}

export function getDocumentUserTypeDisplay(userType: DocumentUserType): string {
  switch (userType) {
    case DocumentUserType.adult:
      return 'Adult';
    case DocumentUserType.adultWithMinors:
      return 'Adult with Minor(s)';
    case DocumentUserType.minors:
      return 'Adult Non-Participant with Minor(s)';
    case DocumentUserType.unsupervisedMinors:
      return 'Adult Non-Participant with Unsupervised Minor(s)';
    case DocumentUserType.adultWithSupervisedAdults:
      return 'Adult Non-Participant with Supervised Adults';
    case DocumentUserType.supervisor:
      return 'Supervisor';
    case DocumentUserType.supervisorWithSupervisee:
      return 'Supervisor with Supervisee';
    default:
      return '';
  }
}

/**
 * Text colour per document status, shared by the Documents report and the
 * customer's document modal so the two cannot drift.
 */
export const DOCUMENT_STATUS_TEXT_CLASSES = {
  incomplete: 'text-muted',
  pending: 'text-warning',
  denied: 'text-danger',
  expired: 'text-danger',
  invalid: 'text-danger',
  valid: 'text-success',
};

export enum DocumentReportStatus {
  valid = 'VAL',
  pending = 'PEN',
  denied = 'DEN',
  expired = 'EXP',
  invalid = 'INV',
}

export enum DocumentReportRole {
  participant = 'PAR',
  minor = 'MIN',
  unsupervisedMinor = 'UMI',
  supervisedAdult = 'SAD',
  nonParticipant = 'NON',
  supervisor = 'SUP',
  supervisee = 'SWE',
}

export enum DocumentCompletedVia {
  kiosk = 'KIO',
  climberApp = 'APP',
  climberAppStaff = 'APS',
  staffSite = 'STA',
}

/** One Documents report row: a (document, customer) pair. */
export interface DocumentReportRow {
  id: number;
  document_id: string;
  customer_id: number;
  customer_first_name: string;
  customer_last_name: string;
  role: DocumentReportRole;
  document_template_id: number;
  document_template_name: string;
  document_type: DocumentType;
  template_version_name: string;
  completed_at: string;
  status: DocumentReportStatus;
  expiration_date: string | null;
  requires_staff_approval: boolean;
  approved_by_first_name: string | null;
  approved_by_last_name: string | null;
  approved_at: string | null;
  denied_by_first_name: string | null;
  denied_by_last_name: string | null;
  denied_at: string | null;
  completed_via: DocumentCompletedVia | null;
  proficiency_names: string;
  other_customer_names: string;
  pdf_url: string | null;
}

/** Multi-select filters are CSV strings of codes, matched any-of. */
export interface DocumentReportFilters {
  document_template_id_in?: string;
  document_type_in?: string;
  status_in?: string;
  role_in?: string;
  completed_via_in?: string;
  requires_staff_approval?: boolean;
  customer_id?: number;
  completed_min?: string;
  completed_max?: string;
  completed_range?: string;
  expiration_min?: string;
  expiration_max?: string;
  expiration_range?: string;
}

export function loadDocumentReportFiltersFromGetParams(activatedRoute: ActivatedRoute): DocumentReportFilters {
  const filters: DocumentReportFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'document_template_id_in', initialQPs);
  loadStringFilterFromGetParams(filters, 'document_type_in', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadStringFilterFromGetParams(filters, 'role_in', initialQPs);
  loadStringFilterFromGetParams(filters, 'completed_via_in', initialQPs);
  loadBoolFilterFromGetParams(filters, 'requires_staff_approval', initialQPs);
  loadIntFilterFromGetParams(filters, 'customer_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'completed_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'completed_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'completed_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'expiration_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'expiration_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'expiration_range', initialQPs);

  return filters;
}

export function getDocumentReportStatusDisplay(status: DocumentReportStatus | string | null): string {
  switch (status) {
    case DocumentReportStatus.valid:
      return 'Valid';
    case DocumentReportStatus.pending:
      return 'Pending Staff Review';
    case DocumentReportStatus.denied:
      return 'Denied by Staff';
    case DocumentReportStatus.expired:
      return 'Expired';
    case DocumentReportStatus.invalid:
      return 'Invalid';
    default:
      return '';
  }
}

export function getDocumentReportStatusClass(status: DocumentReportStatus | string | null): string {
  switch (status) {
    case DocumentReportStatus.valid:
      return DOCUMENT_STATUS_TEXT_CLASSES.valid;
    case DocumentReportStatus.pending:
      return DOCUMENT_STATUS_TEXT_CLASSES.pending;
    case DocumentReportStatus.denied:
      return DOCUMENT_STATUS_TEXT_CLASSES.denied;
    case DocumentReportStatus.expired:
      return DOCUMENT_STATUS_TEXT_CLASSES.expired;
    case DocumentReportStatus.invalid:
      return DOCUMENT_STATUS_TEXT_CLASSES.invalid;
    default:
      return '';
  }
}

export function getDocumentReportRoleDisplay(role: DocumentReportRole | string | null): string {
  switch (role) {
    case DocumentReportRole.participant:
      return 'Participant';
    case DocumentReportRole.minor:
      return 'Minor';
    case DocumentReportRole.unsupervisedMinor:
      return 'Unsupervised Minor';
    case DocumentReportRole.supervisedAdult:
      return 'Supervised Adult';
    case DocumentReportRole.nonParticipant:
      return 'Non-Participant';
    case DocumentReportRole.supervisor:
      return 'Supervisor';
    case DocumentReportRole.supervisee:
      return 'Supervisee';
    default:
      return '';
  }
}

export function getDocumentCompletedViaDisplay(completedVia: DocumentCompletedVia | string | null): string {
  switch (completedVia) {
    case DocumentCompletedVia.kiosk:
      return 'Kiosk';
    case DocumentCompletedVia.climberApp:
      return 'Climber App';
    case DocumentCompletedVia.climberAppStaff:
      return 'Climber App (Staff)';
    case DocumentCompletedVia.staffSite:
      return 'Staff Site';
    default:
      return '';
  }
}
